/**
 * useReportsController — the Reports tab's live leg: the project's filed-report
 * list, filing and re-filing, PDF download, window previews, and the per-item
 * include/exclude curation that waits for its correction to land before the
 * preview is trusted again.
 *
 * Every user-visible outcome goes through `setComingSoon`, the shell's one
 * message surface, so a tap that files, fails, or can't run is never silent.
 * `projectScopeRef` is the shell's project generation: an answer that comes
 * back after the project changed is dropped instead of shown against the wrong
 * project.
 */

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { UsageClient } from "../analytics/usage-client";
import { submitCorrection } from "../components/DailyLog";
import type { ReportEntry, ReportIncludedItem, ReportKind, ReportSidecar } from "../reports/report-types";
import { getOutboxRecord } from "../outbox/db";
import type { OutboxRecord } from "../outbox/types";
import { findFoldedCanonical } from "../read/log-fold";
import {
  asOfFromWindowKey,
  filedDownloadName,
  filingForParent,
  fileableWindowFromFiled,
  makeReportClient,
  type FileOutcome,
  type FileableWindow,
  type FiledReport,
} from "../reports/report-client";
import { deserializeDest, serializeDest } from "../triage/serialize-dest";

export interface ReportsControllerDeps {
  project_ref: string | null;
  projectScopeRef: { readonly current: { project_ref: string | null; generation: number } };
  tenant_id: string;
  user_id: string;
  usageClient: UsageClient | null;
  setComingSoon: (message: string) => void;
  outboxRecords: OutboxRecord[];
  refreshOutbox: () => Promise<void>;
  /** The server's log rows as records, for folding local corrections over. */
  authoritativeServerLogRows: OutboxRecord[];
  serverOrdinalOf: (capture_id: string) => number;
  releaseForDelivery: (capture_id: string) => Promise<boolean>;
  skill_version: string;
}

export interface FiledState {
  project_ref: string | null;
  status: "loading" | "ready" | "error";
  items: FiledReport[];
}

export function useReportsController({
  project_ref,
  projectScopeRef,
  tenant_id,
  user_id,
  usageClient,
  setComingSoon,
  outboxRecords,
  refreshOutbox,
  authoritativeServerLogRows,
  serverOrdinalOf,
  releaseForDelivery,
  skill_version,
}: ReportsControllerDeps) {
  const [reportClient] = useState(() => makeReportClient());

  /** #5: file a report's PDF into the deliverable folder.
   *
   * Reports are not sent by Mark; they land in a directory Mercer mounts and a
   * human attaches them by hand. So the ack names the file that now exists rather
   * than saying "sent" — a superintendent who just approved a CLIENT report needs
   * to know exactly what was produced, and "sent" would be a false statement about
   * where it went. A failure says so plainly instead of leaving the tap silent. */
  /** #15: what is already in the deliverable folder for this project. Read from
   * the server, because the folder IS the record of filing — nothing else records
   * that it happened. Refreshed after every successful file so the "Filed" list
   * reflects the tap that just happened rather than waiting for a wake event. */
  const [filedState, setFiledState] = useState<FiledState>({ project_ref: null, status: "ready", items: [] });
  const filedRequestRef = useRef(0);
  const filedProjectRef = useRef(project_ref);
  filedProjectRef.current = project_ref;
  const refreshFiled = async (): Promise<void> => {
    const targetProject = project_ref;
    const request = ++filedRequestRef.current;
    if (!reportClient || targetProject === null) {
      setFiledState({ project_ref: targetProject, status: "ready", items: [] });
      return;
    }
    setFiledState({ project_ref: targetProject, status: "loading", items: [] });
    const outcome = await reportClient.list(targetProject);
    // A failed list keeps whatever we were showing: rendering [] would say "you
    // have filed nothing", which is the same dangerous lie as an empty read.
    if (request === filedRequestRef.current && filedProjectRef.current === targetProject) {
      setFiledState({
        project_ref: targetProject,
        status: outcome.ok ? "ready" : "error",
        items: outcome.ok ? outcome.items : [],
      });
    }
  };
  useEffect(() => {
    void refreshFiled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project_ref, reportClient]);
  const currentFiledState = filedState.project_ref === project_ref
    ? filedState
    : { project_ref, status: "loading" as const, items: [] };

  /** What went INTO one filed report. `null` is "could not read it", never "it
   * was empty" — the view renders those differently on purpose, because only one
   * of them is a reason to go open the folder. */
  const fetchFiledSidecar = async (f: FiledReport): Promise<ReportSidecar | null> => {
    if (!reportClient || project_ref === null) return null;
    const outcome = await reportClient.sidecar(project_ref, f);
    return outcome.ok ? outcome.sidecar : null;
  };
  const fetchReportPhoto = useMemo(
    () => reportClient && project_ref !== null
      ? (mediaRef: string) => reportClient.photo(project_ref, mediaRef)
      : undefined,
    [project_ref, reportClient],
  );

  /** Download the durable PDF through the same authenticated coordinator seam as
   * the native filed-report read. The PWA never parses or re-encodes the bytes. */
  const downloadFiled = async (f: FiledReport): Promise<void> => {
    if (!reportClient || project_ref === null) {
      setComingSoon("This report can't be downloaded until a project and coordinator are available.");
      return;
    }
    const scope = projectScopeRef.current;
    const outcome = await reportClient.download(project_ref, f);
    if (projectScopeRef.current !== scope) return;
    if (!outcome.ok) {
      setComingSoon(
        outcome.failure === "transport"
          ? "Couldn't download the PDF — no connection. Try again when you have signal."
          : `Couldn't download the PDF (${outcome.error}).`,
      );
      return;
    }
    const url = URL.createObjectURL(new Blob([outcome.bytes], { type: "application/pdf" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    const downloadName = filedDownloadName(f);
    anchor.download = downloadName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    setComingSoon(`Downloaded ${downloadName}.`);
    void usageClient?.track("report_downloaded", project_ref);
  };

  /** What WOULD go into a window that has not been filed yet. Same projection the
   * filing call runs, minus the emit — so this can never disagree with the report
   * it previews. `null` is "could not read it", never "it would be empty". */
  const fetchWindowPreview = async (w: FileableWindow, signal?: AbortSignal, includeEligible = false): Promise<ReportSidecar | null> => {
    if (!reportClient || project_ref === null) return null;
    const outcome = await reportClient.preview(project_ref, w, signal, includeEligible);
    return outcome.ok ? outcome.sidecar : null;
  };

  const setReportItemIncluded = async (
    capture_id: string,
    report_kind: ReportKind,
    included: boolean,
    signal?: AbortSignal,
    window?: FileableWindow,
    item?: ReportIncludedItem,
  ): Promise<boolean> => {
    if (project_ref === null || !window || report_kind === "client" || signal?.aborted) return false;
    const wait = (ms: number) => new Promise<void>((resolve) => {
      if (signal?.aborted) return resolve();
      const onAbort = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      }, ms);
      signal?.addEventListener("abort", onAbort, { once: true });
    });
    // Fold local correction carriers over the latest server canonical before
    // deriving membership. The server read can lag a just-delivered carrier;
    // consulting either source independently would resurrect the stale flags.
    const nativeKind = item?.curation_kind;
    const nativeRecord: OutboxRecord | null = nativeKind ? {
      capture_id,
      client_id: "",
      state: "delivered",
      createdAt: new Date().toISOString(),
      retryAt: 0,
      retryCount: 0,
      parts_delivered: {},
      envelope: {
        type: "CaptureLogged",
        schema_version: "1.0",
        capture_id,
        client_id: "",
        tenant_id: tenant_id,
        ts: new Date().toISOString(),
        skill: "daily_field_log_capture",
        skill_version: skill_version,
        source_refs: [capture_id],
        media: [],
        parts: [],
        payload: {
          project_ref,
          report_membership_target_kind: nativeKind,
          ...(item?.report_dest === undefined ? {} : { report_dest: item.report_dest }),
        },
      },
    } as OutboxRecord : null;
    const record = nativeRecord ?? findFoldedCanonical(authoritativeServerLogRows, outboxRecords, capture_id);
    if (!record) return false;

    // Action/Decision membership is attached to that exact section occurrence,
    // even when the row fanned out from the same capture shown in Activities.
    // The projected report_dest is its effective starting membership (inherited
    // from the source until first curated), so changing Daily preserves Weekly.
    const existing = deserializeDest(record.envelope.payload, { legacyEffective: !nativeKind });
    const reports = nativeKind
      ? {
          daily: item?.report_dest?.includes("daily") ?? false,
          weekly: item?.report_dest?.includes("weekly") ?? false,
        }
      : existing.choice.kind === "reports"
        ? { daily: existing.choice.daily, weekly: existing.choice.weekly }
        : { daily: false, weekly: false };
    reports[report_kind] = included;
    const edits = serializeDest({ choice: { kind: "reports", ...reports }, client: existing.client }, "edit");
    try {
      const carrier = await submitCorrection(
        record,
        "",
        { ...edits, ...(nativeKind ? { report_membership_target_kind: nativeKind } : {}) },
        nativeKind ? (item?.membership_change_ordinal ?? 0) : serverOrdinalOf(capture_id),
      );
      if (!await releaseForDelivery(carrier.capture_id)) return false;

      // Re-exporting before the correction lands can faithfully regenerate the
      // old report, which is a rather elaborate way to ignore the user's edit.
      // A queued durable correction is PENDING, never failed because a wall-clock
      // deadline elapsed. Keep the curation surface busy until it is terminal.
      while (!signal?.aborted) {
        const current = await getOutboxRecord(carrier.capture_id);
        if (!current || current.state === "held" || current.state === "rejected") return false;
        if (current.state === "delivered") {
          while (!signal?.aborted) {
            const projection = await fetchWindowPreview(window, signal);
            if (projection === null) {
              await wait(500);
              continue;
            }
            const occurrences = (projection.sections ?? [])
              .filter((section) => section.curatable === true)
              .flatMap((section) => section.items)
              .filter((candidate) => item?.occurrence_id
                ? candidate.occurrence_id === item.occurrence_id
                : nativeKind
                  ? candidate.curation_kind === nativeKind
                    && (candidate.curation_target_id ?? candidate.capture_id) === capture_id
                  : candidate.curation_kind === undefined && candidate.capture_id === capture_id)
              .length;
            if (included ? occurrences > 0 : occurrences === 0) {
              await refreshOutbox();
              return true;
            }
            await wait(250);
          }
          return false;
        }
        await wait(150);
      }
    } catch {
      return false;
    }
    return false;
  }

  /** #15: file one of the derived fileable windows. The §4.7 client confirm has
   * already happened inside ReportsView by the time this runs. */
  async function fileWindow(w: FileableWindow): Promise<void> {
    await fileFor(w.as_of, w.report_kind);
  }

  function filedMessage(name: string, outcome: Extract<FileOutcome, { ok: true }>): string {
    if (outcome.email_status === "sent") {
      return `Filed to the reports folder as ${name}. Emailed to ${outcome.email_recipient_count} recipient${outcome.email_recipient_count === 1 ? "" : "s"}.`;
    }
    if (outcome.email_status === "already_sent") {
      return `Filed to the reports folder as ${name}. Email was already sent for this exact report.`;
    }
    return `Filed to the reports folder as ${name}. Email delivery is incomplete; download the PDF and send it manually.`;
  }

  /** #19: re-file an already-filed report, from its own detail screen.
   *
   * `as_of` is derived from the FILED report's own date slug, never the clock:
   * re-filing Tuesday's daily must produce Tuesday's daily, and today's date
   * would quietly file a different window under a plausible-looking name. */
  async function refileFiled(f: FiledReport): Promise<boolean> {
    const window = fileableWindowFromFiled(f);
    if (window === null) {
      // Rather than guess a window and re-file the wrong one.
      setComingSoon(`Couldn't work out the date range for "${f.date_slug}".`);
      return false;
    }
    return fileFor(window.as_of, window.report_kind, f);
  }

  /** The one filing call. Both entry points (a fileable window, and a
   * ReportEntry row) funnel through here so the ack wording, the error wording
   * and the post-file refresh cannot drift apart. */
  async function fileFor(
    as_of: string,
    kind: "daily" | "weekly" | "client",
    parent?: FiledReport,
  ): Promise<boolean> {
    if (project_ref === null) {
      setComingSoon("Pick a project first — a report is filed under its project folder.");
      return false;
    }
    if (!reportClient) {
      setComingSoon("This build has no coordinator endpoint, so nothing can be filed.");
      return false;
    }
    const scope = projectScopeRef.current;
    const outcome = await reportClient.file({
      filing_scope: JSON.stringify([tenant_id, user_id]),
      project_ref,
      display: project_ref,
      as_of,
      cadence: kind === "daily" ? "day" : "week",
      audience: kind === "client" ? "client" : "internal",
      filing: filingForParent(parent),
    });
    if (projectScopeRef.current !== scope) return false;
    if (outcome.ok) {
      const name = outcome.pdf.split("/").pop() ?? outcome.pdf;
      setComingSoon(filedMessage(name, outcome));
      await refreshFiled();
      void usageClient?.track("report_filed", project_ref);
      return true;
    } else if (outcome.failure === "transport") {
      await refreshFiled();
      setComingSoon("Filing result unknown. Check Filed reports after reconnecting: if a newer generation appears, it already landed. Use Edit & re-export only to deliberately create another revision.");
    } else if (outcome.retryable) {
      await refreshFiled();
      setComingSoon(`Filing result unknown (${outcome.error}). Check Filed reports: if a newer generation appears, it already landed. Use Edit & re-export only to deliberately create another revision.`);
    } else if (outcome.error === "status_conflict") {
      await refreshFiled();
      setComingSoon("That report is no longer the newest revision. Open the current generation before editing and re-exporting.");
    } else {
      setComingSoon(`Couldn't file it — the server refused (${outcome.error}).`);
    }
    return false;
  }

  async function fileReport(entry: ReportEntry): Promise<void> {
    if (project_ref === null) {
      // NOT a silent return: a visible button that does nothing reads as the app
      // being frozen, which is the whole reason the coming-soon surface exists.
      setComingSoon("Pick a project first — a report is filed under its project folder.");
      return;
    }
    if (!reportClient) {
      setComingSoon("This build has no coordinator endpoint, so nothing can be filed.");
      return;
    }
    const as_of = asOfFromWindowKey(entry.window_key);
    if (!as_of) {
      // Rather than guess a window from today's clock and file the wrong report.
      setComingSoon(`Couldn't work out the date range for "${entry.window_key}".`);
      return;
    }
    const outcome = await reportClient.file({
      filing_scope: JSON.stringify([tenant_id, user_id]),
      project_ref,
      display: project_ref,
      as_of,
      cadence: entry.report_kind === "daily" ? "day" : "week",
      audience: entry.report_kind === "client" ? "client" : "internal",
      filing: { protocol: "parent-anchored-v1", intent: "initial" },
    });
    if (outcome.ok) {
      const name = outcome.pdf.split("/").pop() ?? outcome.pdf;
      setComingSoon(filedMessage(name, outcome));
      void usageClient?.track("report_filed", project_ref);
    } else if (outcome.failure === "transport") {
      await refreshFiled();
      setComingSoon("Filing result unknown. Check Filed reports after reconnecting: if a newer generation appears, it already landed. Use Edit & re-export only to deliberately create another revision.");
    } else if (outcome.retryable) {
      await refreshFiled();
      setComingSoon(`Filing result unknown (${outcome.error}). Check Filed reports: if a newer generation appears, it already landed. Use Edit & re-export only to deliberately create another revision.`);
    } else if (outcome.error === "status_conflict") {
      await refreshFiled();
      setComingSoon("That report is no longer the newest revision. Open the current generation before editing and re-exporting.");
    } else {
      setComingSoon(`Couldn't file it — the server refused (${outcome.error}).`);
    }
  }
  return {
    filedState: currentFiledState,
    fetchFiledSidecar,
    fetchReportPhoto,
    downloadFiled,
    fetchWindowPreview,
    setReportItemIncluded,
    fileWindow,
    refileFiled,
    fileReport,
  };
}
